import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TenantContext = createContext(null);

function hexToRgb(hex) {
    const h = hex.replace("#", "");
    const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

function pickFg(hex) {
    const [r, g, b] = hexToRgb(hex);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 160 ? "#1A1A2E" : "#FFFFFF";
}

export function applyTenantAccent(color) {
    if (!color) return;
    const fg = pickFg(color);
    const [r, g, b] = hexToRgb(color);
    document.documentElement.style.setProperty("--tenant-accent", color);
    document.documentElement.style.setProperty("--tenant-accent-foreground", fg);
    document.documentElement.style.setProperty("--tenant-accent-soft", `rgba(${r}, ${g}, ${b}, 0.14)`);
}

export function TenantProvider({ children }) {
    const { user } = useAuth();
    const [tenants, setTenants] = useState([]);
    const [activeTenant, setActiveTenantState] = useState(null);
    const [loaded, setLoaded] = useState(false);

    const setActiveTenant = useCallback((t) => {
        setActiveTenantState(t);
        if (t) {
            localStorage.setItem("inventur_tenant", t.tenant_id);
            applyTenantAccent(t.color);
        }
    }, []);

    const loadTenants = useCallback(async () => {
        try {
            const r = await api.get("/tenants");
            setTenants(r.data);
            const stored = localStorage.getItem("inventur_tenant");
            if (stored) {
                const t = r.data.find((x) => x.tenant_id === stored);
                if (t) setActiveTenant(t);
            }
        } catch (_) {
            // ignore (not authenticated)
        } finally {
            setLoaded(true);
        }
    }, [setActiveTenant]);

    useEffect(() => {
        if (user) {
            loadTenants();
        } else {
            setTenants([]);
            setActiveTenantState(null);
            setLoaded(true);
        }
    }, [user, loadTenants]);

    const clearTenant = () => {
        setActiveTenantState(null);
        localStorage.removeItem("inventur_tenant");
    };

    return (
        <TenantContext.Provider value={{ tenants, activeTenant, setActiveTenant, clearTenant, reload: loadTenants, loaded }}>
            {children}
        </TenantContext.Provider>
    );
}

export const useTenant = () => useContext(TenantContext);
